import { useState } from "react";
import axios from "axios";
import { ExternalLink, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Reveal } from "@/components/Reveal";
import { INFO, BOOKABLE_GROUPS } from "@/data/site";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Buchung() {
  const { t } = useLang();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", service: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const first = form.first_name.trim();
    const last = form.last_name.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    const service = form.service.trim();
    if (first.length < 2 || last.length < 2) return toast.error("Bitte Vor- und Nachname eintragen.");
    if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(email)) return toast.error("Bitte eine gültige E-Mail eintragen.");
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 7 || digits.length > 15) return toast.error("Bitte eine gültige Handynummer eintragen.");
    if (!service) return toast.error("Bitte gewünschte Dienstleistung auswählen.");

    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, {
        first_name: first, last_name: last, email, phone, service, notes: form.notes.trim(),
      });
      setDone(true);
    } catch {
      toast.error("Ups, das hat nicht geklappt. Bitte kurz später erneut probieren.");
    } finally { setLoading(false); }
  };

  const services = BOOKABLE_GROUPS.flatMap((g) => g.items);

  return (
    <div data-testid="booking-page" className="min-h-screen pt-24 pb-24 bg-background">
      <div className="mx-auto max-w-4xl px-6 md:px-10">
        <Reveal>
          <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">{t("book.eyebrow")}</span>
          <h1 className="font-serif font-light text-5xl md:text-6xl tracking-tighter mt-4 leading-[1]">
            {t("book.title")}
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg text-foreground/70 leading-relaxed">
            {t("book.intro")}
          </p>
        </Reveal>

        {/* Shore direct booking */}
        <Reveal delay={0.1}>
          <div className="mt-12 border border-border bg-muted/30 p-7 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="flex-1">
              <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-[0.25em]">
                <Sparkles strokeWidth={1.5} size={14} /> {t("book.shore_eyebrow")}
              </div>
              <h2 className="font-serif text-2xl md:text-3xl mt-3">{t("book.shore_title")}</h2>
              <p className="text-sm text-foreground/70 mt-2 max-w-xl leading-relaxed">{t("book.shore_body")}</p>
            </div>
            <a
              href={INFO.shore} target="_blank" rel="noreferrer" data-testid="shore-direct-btn"
              className="group inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-8 py-4 text-sm tracking-wide whitespace-nowrap hover:opacity-90 transition-opacity"
            >
              {t("book.shore_cta")} <ExternalLink size={15} className="transition-transform group-hover:translate-x-0.5" />
            </a>
          </div>
        </Reveal>

        {/* Waitlist */}
        <Reveal delay={0.15}>
          <div className="mt-16 border-t border-border pt-14">
            <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">{t("book.wl_eyebrow")}</span>
            <h2 className="font-serif font-light text-3xl md:text-4xl tracking-tight mt-3">{t("book.wl_title")}</h2>
            <p className="mt-3 max-w-xl text-foreground/70 leading-relaxed">{t("book.wl_body")}</p>

            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                data-testid="waitlist-success"
                className="mt-10 border border-primary/30 bg-primary/5 p-8 flex items-start gap-4"
              >
                <CheckCircle2 strokeWidth={1.5} className="text-primary shrink-0 mt-1" />
                <div>
                  <h3 className="font-serif text-xl">{t("book.wl_success_title")}</h3>
                  <p className="text-sm text-foreground/70 mt-2 leading-relaxed">{t("book.wl_success_body")}</p>
                </div>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="mt-8 grid gap-4 max-w-2xl" data-testid="waitlist-form">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input data-testid="wl-first" placeholder={t("book.wl_first")} value={form.first_name} onChange={update("first_name")} className="h-13 rounded-none bg-background border-border py-3" autoComplete="given-name" />
                  <Input data-testid="wl-last" placeholder={t("book.wl_last")} value={form.last_name} onChange={update("last_name")} className="h-13 rounded-none bg-background border-border py-3" autoComplete="family-name" />
                </div>
                <Input data-testid="wl-email" type="email" inputMode="email" autoComplete="email" placeholder={t("book.wl_email")} value={form.email} onChange={update("email")} className="h-13 rounded-none bg-background border-border py-3" />
                <Input data-testid="wl-phone" type="tel" inputMode="tel" autoComplete="tel" placeholder={t("book.wl_phone")} value={form.phone} onChange={update("phone")} className="h-13 rounded-none bg-background border-border py-3" />
                <select
                  data-testid="wl-service"
                  value={form.service}
                  onChange={update("service")}
                  className="h-13 rounded-none bg-background border border-border px-3 text-sm py-3"
                >
                  <option value="">{t("book.wl_service_placeholder")}</option>
                  {services.map((s) => (
                    <option key={s.name} value={s.name}>{s.name}</option>
                  ))}
                </select>
                <Textarea data-testid="wl-notes" placeholder={t("book.wl_notes")} value={form.notes} onChange={update("notes")} className="rounded-none bg-background border-border min-h-[100px]" />
                <button
                  type="submit" disabled={loading} data-testid="wl-submit"
                  className="mt-2 bg-primary text-primary-foreground px-8 py-4 text-sm tracking-wide disabled:opacity-60"
                >
                  {loading ? "…" : t("book.wl_submit")}
                </button>
                <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">
                  {t("book.wl_privacy")}
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
